import {Options} from '@fl/cmsch-fe-library';
import {opt} from 'ts-opt';

import {CabFarmAutocomplete} from 'api/gen/CabFarmAutocomplete';
import {CabStableAutocomplete} from 'api/gen/CabStableAutocomplete';
import {Organization} from 'api/gen/Organization';

/**
 * Converts stable autocomplete rows into select options for the comprehensive browser filters.
 *
 * @param items Stable autocomplete rows returned by the API.
 * @returns Stable select options with code, optional name, and optional business name.
 */
export const genStableOptions = (items: Array<CabStableAutocomplete>): Options<number> =>
    items.map(item => {
        const businessName = opt(item.subjectBusinessName).map(x => ` (${x})`).orElse('');
        const name = opt(item.name).map(x => ` - ${x}`).orElse('');

        return {
            value: item.id,
            label: `${item.code}${name}${businessName}`,
        };
    });

/**
 * Converts farm autocomplete rows into select options for the comprehensive browser filters.
 *
 * @param items Farm autocomplete rows returned by the API.
 * @returns Farm select options with code, optional name, and optional business name.
 */
export const genFarmOptions = (items: Array<CabFarmAutocomplete>): Options<number> =>
    items.map(item => {
        const businessName = opt(item.subjectBusinessName).map(x => ` (${x})`).orElse('');
        const name = opt(item.name).map(x => ` - ${x}`).orElse('');

        return {
            value: item.id,
            label: `${item.code}${name}${businessName}`,
        };
    });

/**
 * Converts organization rows into select options for the comprehensive browser filters.
 *
 * @param items Organizations returned by the API.
 * @returns Organization select options with code and optional name.
 */
export const genOrganizationOptions = (items: Array<Organization>): Options<number> =>
    items.map(item => ({
        value: item.id,
        label: `${item.code}${item.name ? ` - ${item.name}` : ''}`,
    }));
